import { FiX } from "react-icons/fi";
import { formatSalesValue } from "../../../utils/formatUtils";

export const ReceivedOrderModal = ({ order, onClose }) => {
    if (!order) return null;

    const items = order.items || [];
    const taxableItems = items.filter(i => i.Taxable?.toLowerCase() === "yes");
    const nonTaxableItems = items.filter(i => i.Taxable?.toLowerCase() !== "yes");

    const formatDate = (raw) => {
        if (!raw) return "—";
        return new Date(raw).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="flex items-start justify-between px-6 py-3 border-b bg-gray-50 rounded-lg">
                    <div>
                        <div className="text-base font-semibold text-gray-800 tracking-wider">
                            Purchase Order — GRN #{order.GRN}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5 font-semibold tracking-wide">
                            Bill #{order.purchaseBillNumber} &nbsp;·&nbsp; {order.RequisitionType}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-md hover:bg-gray-200 text-gray-500 transition-colors">
                        <FiX size={18} />
                    </button>
                </div>

                {/* Meta grid */}
                <div className="px-6 py-3 border-b grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Supplier</div>
                        <div className="font-medium text-gray-700">{order.Company_Name || "—"}</div>
                        {order.company_pan && <div className="text-xs text-gray-400">PAN: {order.company_pan}</div>}
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Outlet</div>
                        <div className="font-medium text-gray-700">{order.Outlet_Name}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Order Date</div>
                        <div className="font-medium text-gray-700">{formatDate(order.Date)}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Received Date</div>
                        <div className="font-medium text-gray-700">{formatDate(order.ReceivedDate)}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Payment Mode</div>
                        <div className="font-medium text-gray-700">{order.payment_mode || "—"}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Status</div>
                        <span className="inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 font-medium">
                            {order.State}
                        </span>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Units Ordered</div>
                        <div className="font-medium text-gray-700">{order.total_units_ordered}</div>
                    </div>
                    <div>
                        <div className="text-xs text-gray-400 uppercase tracking-widest mb-0.5 font-semibold">Units Received</div>
                        <div className="font-medium text-gray-700">{order.total_units_received}</div>
                    </div>
                </div>

                {/* Items table */}
                <div className="flex-1 overflow-auto px-6 py-3 tiny-scrollbar">
                    <table className="w-full text-sm text-center rounded-md overflow-hidden shadow-md">
                        <thead className="bg-gray-100 sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-2.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wider">#</th>
                                <th className="px-3 py-2.5 text-left font-semibold text-gray-600 text-xs uppercase tracking-wider">Item</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">Brand</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">Group</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">UOM</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">Qty</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">Rate</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">Taxable</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">Exp Date</th>
                                <th className="px-3 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wider">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((item, idx) => {
                                const qty = parseFloat(item.UnitsOrdered || 0);
                                const rate = parseFloat(item.Rate || 0);
                                const amount = qty * rate;
                                const isTaxable = item.Taxable?.toLowerCase() === "yes";

                                return (
                                    <tr key={idx} className="hover:bg-gray-50 transition">
                                        <td className="px-3 py-2 text-left text-gray-400">{idx + 1}</td>
                                        <td className="px-3 py-2 text-left">
                                            <div className="font-medium text-gray-800">{item.Name}</div>
                                            <div className="text-xs text-gray-600 tracking-wide">{item.Department} <br /> {item.StockType}</div>
                                        </td>
                                        <td className="px-3 py-2 text-gray-600">{item.BrandName || "—"}</td>
                                        <td className="px-3 py-2 text-gray-600">{item.GroupName}</td>
                                        <td className="px-3 py-2 text-gray-600">{item.UOM}</td>
                                        <td className="px-3 py-2 text-gray-700 font-medium">{qty}</td>
                                        <td className="px-3 py-2 text-gray-700">{formatSalesValue(rate)}</td>
                                        <td className="px-3 py-2">
                                            <span className={`inline-block px-2 py-1 text-xs rounded font-medium ${isTaxable ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"}`}>
                                                {isTaxable ? "Yes" : "No"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-gray-600 text-xs font-semibold">{formatDate(item.ExpDate)}</td>
                                        <td className="px-3 py-2 font-medium text-gray-800">{formatSalesValue(amount)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Footer totals */}
                <div className="px-5 py-3 border-t bg-gray-50 rounded-b-xl flex flex-wrap justify-end gap-8 text-sm">
                    {taxableItems.length > 0 && (
                        <div className="text-right">
                            <div className="text-xs text-gray-600 uppercase font-semibold tracking-wide">Taxable Subtotal</div>
                            <div className="font-medium text-gray-800">
                                {formatSalesValue(taxableItems.reduce((s, i) => s + parseFloat(i.UnitsOrdered || 0) * parseFloat(i.Rate || 0), 0))}
                            </div>
                        </div>
                    )}
                    {nonTaxableItems.length > 0 && (
                        <div className="text-right">
                            <div className="text-xs text-gray-600 uppercase font-semibold tracking-wide">Non-Taxable Subtotal</div>
                            <div className="font-medium text-gray-800">
                                {formatSalesValue(nonTaxableItems.reduce((s, i) => s + parseFloat(i.UnitsOrdered || 0) * parseFloat(i.Rate || 0), 0))}
                            </div>
                        </div>
                    )}
                    <div className="text-right">
                        <div className="text-xs text-gray-600 uppercase font-semibold tracking-wide">Tax (13%)</div>
                        <div className="font-medium text-gray-800">{formatSalesValue(order.TaxAmount)}</div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-gray-600 uppercase font-semibold tracking-wide">Grand Total</div>
                        <div className="text-base font-bold text-gray-900">{formatSalesValue(order.TotalAmount)}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};